import { assertValue } from '@shared/helpers'
import { AppId } from '@shared/types'
import { CommonWizardWrapper } from '@ui-common/components/CommonWizardWrapper'

import { LoanOfferAnalyticsProvider, useLoanOfferAnalyticsService } from '../analytics/analyticsContext'
import { BookingStep } from '../steps/booking/BookingStep'
import { FormContext } from '../steps/formContext'
import { GetLoanOfferStep } from '../steps/get-loan-offer/GetLoanOfferStep'
import { InstallmentStep } from '../steps/installment/InstallmentStep'
import { LoanOfferContext } from '../steps/LoanOfferContext'
import { OfferStep } from '../steps/offer/OfferStep'
import { RecommendationStep } from '../steps/recommendation/RecommendationStep'
import { StatusStep } from '../steps/status/StatusStep'
import { TermsInfoStep } from '../steps/terms-info/TermsInfoStep'
import { TheEndStep } from '../steps/the-end/TheEndStep'
import { useWizardLogic } from './useWizardLogic'
import { WizardContext, WizardStep } from './wizardMachine'

type WizardProps = {
  initialContext: WizardContext
  onGoToApplicationsClick: () => void
  onNewApplicationClick: () => void
}

export const Wizard = (props: WizardProps) => {
  return (
    <LoanOfferAnalyticsProvider>
      <LoanOfferWizard {...props} />
    </LoanOfferAnalyticsProvider>
  )
}

const LoanOfferWizard = ({ initialContext, onGoToApplicationsClick, onNewApplicationClick }: WizardProps) => {
  const { actor, ...formContext } = useWizardLogic(initialContext)
  const { analyticsService } = useLoanOfferAnalyticsService()
  const { step, exit, authToken } = formContext

  const stepComponentMap: Record<WizardStep, () => React.ReactNode> = {
    [WizardStep.InitialLoanOfferStep]: () => null,
    [WizardStep.GetLoanOfferStep]: () => <GetLoanOfferStep parentService={actor} />,
    [WizardStep.StatusStep]: () => (
      <StatusStep onGoToApplicationsClick={onGoToApplicationsClick} onNewApplicationClick={onNewApplicationClick} />
    ),
    [WizardStep.OfferStep]: () => <OfferStep />,
    [WizardStep.InstallmentStep]: () => <InstallmentStep />,
    [WizardStep.TermsInfoStep]: () => <TermsInfoStep />,
    [WizardStep.RecommendationStep]: () => <RecommendationStep />,
    [WizardStep.BookingStep]: () => <BookingStep />,
    [WizardStep.TheEndStep]: () => <TheEndStep />,
  }

  const wizard = (
    <CommonWizardWrapper
      stepComponentMap={stepComponentMap}
      step={step}
      appId={AppId.SecLoans}
      analytics={{ analyticsService }}
      onLogout={exit}
      authToken={authToken ?? null}
      userContext={null}
    />
  )

  return (
    <FormContext value={formContext}>
      {formContext.loanOffer ? (
        <LoanOfferContext
          value={{
            ...formContext,
            loanOffer: assertValue(formContext.loanOffer, 'formContext.loanOffer'),
          }}
        >
          {wizard}
        </LoanOfferContext>
      ) : (
        wizard
      )}
    </FormContext>
  )
}
